import type { IClientTimelines } from "@gen/app-query";
import { ClientTimelines, CoreQuery, Query } from "@gen/app-query";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import { DaysHoursFields } from "./DaysHoursFields";

interface OnlineTimelinesProps {
  clientId: string;
  mode: "view" | "edit";
}

type TimelinesFormValues = IClientTimelines;

export function OnlineTimelines({ clientId, mode }: OnlineTimelinesProps) {
  const isViewMode = mode === "view";

  const {
    data: adminTimelinesData,
    isFetching: isAdminTimelinesFetching,
    refetch: refetchAdminTimelines,
  } = CoreQuery.useAdmin_getAdminClientTimelinesQuery(clientId, {
    enabled: !isViewMode,
  });

  const { data: viewTimelinesData, isFetching: isViewTimelinesFetching } =
    Query.useCore_getClientTimelinesQuery(clientId, {
      enabled: isViewMode,
    });

  const timelinesData = isViewMode ? viewTimelinesData : adminTimelinesData;
  const isTimelinesFetching = isViewMode
    ? isViewTimelinesFetching
    : isAdminTimelinesFetching;

  const memoValues = useMemo<TimelinesFormValues>(
    () => ({
      complaint_period_seconds: timelinesData?.complaint_period_seconds,
      delivery_allowance_seconds: timelinesData?.delivery_allowance_seconds,
      tracking_details_allowance_seconds:
        timelinesData?.tracking_details_allowance_seconds,
    }),
    [timelinesData],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<TimelinesFormValues>({
    defaultValues: memoValues,
  });

  useEffect(() => {
    reset(memoValues);
  }, [memoValues, reset]);

  // NOTE We have the handle 404 status here and reset the values
  // because the component has to be rerendered and has to present
  // the placeholder again to the user. Ideally, we would handle this
  // inside the `api` and just return an empty reponse.
  const resetTimelines = async () => {
    if (isViewMode) {
      return;
    }
    try {
      await refetchAdminTimelines({
        throwOnError: true,
      });
    } catch {
      reset({
        complaint_period_seconds: 0,
      });
    }
  };

  const { mutate: updateTimelines, isPending: isUpdateTimelinesPending } =
    CoreQuery.useAdmin_updateClientTimelinesMutation(clientId, {
      onSuccess: () => {
        refetchAdminTimelines();
      },
      onError: () => {
        resetTimelines();
      },
      meta: {
        success: "Timelines updated",
      },
    });

  const onSubmit = (values: TimelinesFormValues) => {
    updateTimelines(
      ClientTimelines.fromJS({
        complaint_period_seconds: Number(values.complaint_period_seconds),
        delivery_allowance_seconds: Number(values.delivery_allowance_seconds),
        tracking_details_allowance_seconds: Number(
          values.tracking_details_allowance_seconds,
        ),
      }),
    );
  };

  const isLoading =
    isTimelinesFetching || (!isViewMode && isUpdateTimelinesPending);
  const isDisabled = !isDirty;

  return (
    <Stack spacing={5}>
      <Typography variant="body1DashedDivider">Online</Typography>
      <Stack spacing={1.5}>
        <Typography variant="body1">Complaint Period</Typography>
        <DaysHoursFields
          fieldName="complaint_period_seconds"
          control={control}
          disabled={isViewMode}
          dataTestIdPrefix="online-complaint-period"
          showMinutes
        />
      </Stack>
      <Stack spacing={1.5}>
        <Typography variant="body1">Delivery Allowance</Typography>
        <DaysHoursFields
          fieldName="delivery_allowance_seconds"
          control={control}
          disabled={isViewMode}
          dataTestIdPrefix="online-delivery-allowance"
        />
      </Stack>
      <Stack spacing={1.5}>
        <Typography variant="body1">Tracking Details Allowance</Typography>
        <DaysHoursFields
          fieldName="tracking_details_allowance_seconds"
          control={control}
          disabled={isViewMode}
          dataTestIdPrefix="online-tracking-allowance"
        />
      </Stack>
      {!isViewMode && (
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={isDisabled}
          size="large"
          data-testid="save-online-timelines-button"
        >
          Save Online Timelines
        </Button>
      )}
    </Stack>
  );
}
